import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import { FaRupeeSign, FaBookOpen } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import SignupModal from "./Login/SignupModal";

const pricingPlans = [
  {
    books: 2,
    monthly: 450,
    sixMonths: [2430, 3030, 3630],
    twelveMonths: [4320, 5520, 6720],
  },
  {
    books: 3,
    monthly: 500,
    sixMonths: [2700, 3300, 3900],
    twelveMonths: [4800, 6000, 7200],
    isPopular: true, // Hostinger-style center focus
  },
  {
    books: 4,
    monthly: 550,
    sixMonths: [2970, 3570, 4170],
    twelveMonths: [5280, 6480, 7680],
  },
];

const LibraryPricingCard = ({
  Member_ID,
  expiryDate,
  userData,
  selectedPlan,
  setSelectedPlan,
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [depositPaid, setDepositPaid] = useState("");
  const [formattedExpiry, setFormattedExpiry] = useState("");
  const todayIST = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
  );
  const [activationDate, setActivationDate] = useState(
    todayIST.toISOString().split("T")[0]
  );
  const [signupOpen, setSignupOpen] = useState(false);

  useEffect(() => {
    if (expiryDate) {
      const [dd, mm, yy] = expiryDate.split("/");
      const fullYear = Number(yy) < 50 ? `20${yy}` : `19${yy}`;
      const isoExpiry = `${fullYear}-${mm}-${dd}`;
      setFormattedExpiry(isoExpiry);
    }
  }, [expiryDate]);

  return (
    <Container className="modern-pricing-section py-4">
      {/* Containerized Hostinger / Wix pricing style configurations */}
      <style>{`
        :root {
          --slate-title: #0f172a;
          --slate-muted: #64748b;
          --brand-primary: #6366f1;
          --brand-success: #22c55e;
          --card-bg: #ffffff;
          --radius-pricing: 24px;
          --smooth-curve: cubic-bezier(0.16, 1, 0.3, 1);
        }

        .pricing-section-tagline {
          display: inline-block;
          padding: 6px 14px;
          background: rgba(99, 102, 241, 0.06);
          color: var(--brand-primary);
          font-weight: 700;
          font-size: 0.85rem;
          border-radius: 100px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 12px;
        }

        .pricing-main-title {
          font-weight: 800;
          color: var(--slate-title);
          letter-spacing: -1px;
        }

        .premium-pricing-card {
          border: 1px solid rgba(0, 0, 0, 0.05) !important;
          border-radius: var(--radius-pricing) !important;
          background: var(--card-bg) !important;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.02);
          transition: all 0.4s var(--smooth-curve) !important;
          position: relative;
          cursor: pointer;
          overflow: hidden;
        }

        .premium-pricing-card:hover {
          transform: translateY(-6px);
          border-color: rgba(99, 102, 241, 0.2) !important;
          box-shadow: 0 25px 50px -12px rgba(99, 102, 241, 0.1) !important;
        }

        /* Highlight Popular / Active state layout rules */
        .premium-pricing-card.is-popular-flag {
          border: 2px solid var(--brand-primary) !important;
          box-shadow: 0 20px 40px rgba(99, 102, 241, 0.08) !important;
        }

        .selected-plan-active {
          border: 2px solid var(--brand-success) !important;
          background: rgba(34, 197, 94, 0.01) !important;
        }

        .popular-ribbon {
          position: absolute;
          top: 14px;
          right: 14px;
          background: var(--brand-primary);
          color: #ffffff;
          font-size: 0.75rem;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 6px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .plan-title-head {
          font-weight: 700;
          color: var(--slate-title);
          font-size: 1.3rem;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .currency-hero-wrap {
          margin: 24px 0;
        }

        .price-huge-digit {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--slate-title);
          letter-spacing: -1px;
        }

        .price-period-label {
          color: var(--slate-muted);
          font-size: 0.95rem;
          font-weight: 500;
        }

        /* Tier breakdown row formatting */
        .sub-tier-block {
          border-top: 1px solid rgba(0, 0, 0, 0.05);
          padding-top: 16px;
          margin-top: 16px;
        }

        .sub-tier-heading {
          font-weight: 700;
          font-size: 0.85rem;
          color: var(--slate-title);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 10px;
        }

        .tier-data-line {
          display: flex;
          justify-content: space-between;
          font-size: 0.9rem;
          margin-bottom: 6px;
          color: var(--slate-muted);
        }
        .tier-data-line span:last-child {
          font-weight: 600;
          color: var(--slate-title);
        }

        /* Modernized Form Elements */
        .modern-admin-form {
          border-radius: 20px !important;
          border: 1px solid rgba(0, 0, 0, 0.05) !important;
          background: #f8fafc !important;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.01) !important;
        }

        .modern-form-label {
          font-weight: 600;
          color: var(--slate-title);
          font-size: 0.9rem;
          margin-bottom: 6px;
        }

        .modern-form-input {
          border-radius: 10px !important;
          border: 1px solid rgba(0, 0, 0, 0.1) !important;
          padding: 10px 14px !important;
          font-size: 0.95rem !important;
          transition: var(--smooth-curve);
        }
        .modern-form-input:focus {
          border-color: var(--brand-primary) !important;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1) !important;
        }

        /* Button structural presets */
        .btn-pricing-action {
          font-weight: 600 !important;
          padding: 12px 24px !important;
          border-radius: 12px !important;
          transition: all 0.3s var(--smooth-curve) !important;
        }
        
        .btn-theme-solid {
          background: var(--slate-900) !important;
          color: #ffffff !important;
          border: none !important;
        }
        .btn-theme-solid:hover {
          background: var(--brand-primary) !important;
          transform: translateY(-2px);
        }

        .btn-theme-active-solid {
          background: var(--brand-success) !important;
          color: #ffffff !important;
          border: none !important;
        }

        /* Info Footer adjustments */
        .pricing-footer-note {
          background: #f8fafc;
          border-radius: 16px;
          padding: 20px;
          border: 1px dashed rgba(0, 0, 0, 0.08);
        }
      `}</style>

      {/* Main Header Display Component Block */}
      <div className="text-center mb-5">
        <span className="pricing-section-tagline">Subscription Plans</span>
        <h2 className="pricing-main-title h1">Membership Tariff Ecosystems</h2>
        <p className="text-muted fs-5">Choose the footprint matching your current physical reading volume framework.</p>
      </div>

      <Row className="gy-4 justify-content-center">
        {pricingPlans.map((plan, index) => {
          const isSelected = selectedPlan === `${plan.books} Books`;
          return (
            <Col lg="4" md="6" key={index} className="px-3">
              <Card
                className={`h-100 premium-pricing-card p-4 ${
                  plan.isPopular ? "is-popular-flag" : ""
                } ${isSelected && Member_ID ? "selected-plan-active" : ""}`}
                onClick={() =>
                  Member_ID && setSelectedPlan(`${plan.books} Books`)
                }
              >
                {plan.isPopular && <div className="popular-ribbon">Most Popular</div>}
                
                <CardBody className="p-0 d-flex flex-column justify-content-between">
                  <div>
                    <div className="plan-title-head">
                      <FaBookOpen style={{ color: "var(--brand-primary)" }} />
                      <span>{plan.books} Books Plan</span>
                    </div>

                    <div className="currency-hero-wrap">
                      <span className="price-huge-digit">₹{plan.monthly}</span>
                      <span className="price-period-label"> / month</span>
                    </div>

                    {/* 6 Month Tier Matrix list block */}
                    <div className="sub-tier-block">
                      <div className="sub-tier-heading">6 Months Plan <span className="text-success">(10% Off)</span></div>
                      <div className="tier-data-line"><span>Standard Plan</span><span>₹{plan.sixMonths[0]}</span></div>
                      <div className="tier-data-line"><span>Door Delivery (x2)</span><span>₹{plan.sixMonths[1]}</span></div>
                      <div className="tier-data-line"><span>Door Delivery (x4)</span><span>₹{plan.sixMonths[2]}</span></div>
                    </div>

                    {/* 12 Month Tier Matrix list block */}
                    <div className="sub-tier-block">
                      <div className="sub-tier-heading">12 Months Plan <span className="text-success">(20% Off)</span></div>
                      <div className="tier-data-line"><span>Standard Plan</span><span>₹{plan.twelveMonths[0]}</span></div>
                      <div className="tier-data-line"><span>Door Delivery (x2)</span><span>₹{plan.twelveMonths[1]}</span></div>
                      <div className="tier-data-line"><span>Door Delivery (x4)</span><span>₹{plan.twelveMonths[2]}</span></div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-muted text-center mb-3" style={{ fontSize: "0.85rem" }}>
                      <span className="fst-italic">One-time refundable deposit: ₹750</span>
                    </p>

                    {currentPath === "/" ? (
                      <Button
                        className="w-100 btn-pricing-action btn-theme-solid"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSignupOpen(true);
                        }}
                      >
                        Get Started
                      </Button>
                    ) : (
                      <Button
                        className={`w-100 btn-pricing-action ${isSelected ? 'btn-theme-active-solid' : 'btn-theme-solid'}`}
                        onClick={() =>
                          Member_ID && setSelectedPlan(`${plan.books} Books`)
                        }
                      >
                        {isSelected ? "Plan Selected ✓" : `Select ${plan.books} Book Plan`}
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Admin Operations Sub-form Container Block */}
      {Member_ID && (
        <Form className="mt-5 modern-admin-form p-4">
          <h4 className="text-start mb-4 fw-bold" style={{ color: 'var(--slate-title)', fontSize: '1.25rem' }}>
            📝 Assign Parameters for Member ID: <span style={{ color: 'var(--brand-primary)' }}>{Member_ID}</span>
          </h4>

          <Row className="gy-3">
            <Col md={6}>
              <FormGroup>
                <Label for="membershipPlan" className="modern-form-label">Membership Plan</Label>
                <Input
                  type="text"
                  id="membershipPlan"
                  className="modern-form-input"
                  value={selectedPlan}
                  readOnly
                  placeholder="Select a plan card above"
                />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="activationDate" className="modern-form-label">Activation Date</Label>
                <Input
                  type="date"
                  id="activationDate"
                  className="modern-form-input"
                  value={activationDate}
                  onChange={(e) => setActivationDate(e.target.value)}
                />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="expiryDate" className="modern-form-label">Expiry Date</Label>
                <Input
                  type="date"
                  id="expiryDate"
                  className="modern-form-input"
                  value={formattedExpiry}
                  onChange={(e) => setFormattedExpiry(e.target.value)}
                />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="depositPaid" className="modern-form-label">Deposit Paid (₹)</Label>
                <Input
                  type="number"
                  id="depositPaid"
                  className="modern-form-input"
                  value={depositPaid}
                  onChange={(e) => setDepositPaid(e.target.value)}
                  placeholder="e.g., 750"
                />
              </FormGroup>
            </Col>
          </Row>
        </Form>
      )}

      {/* Global Metadata Footer Notation Frame */}
      <div className="text-center mt-5 mx-auto" style={{ maxWidth: '600px' }}>
        <div className="pricing-footer-note">
          <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
            <strong>Note:</strong> Standard daily localized rental fee architectures apply automatically after membership expiration dates pass.
          </p>
          <div className="fw-bold text-dark mt-3" style={{ fontSize: '0.95rem' }}>
            Accepted Modes: <span className="text-success">Cash / GPay / PhonePe / Paytm</span>
            <div className="mt-2 text-primary" style={{ fontSize: '1.1rem' }}>
              📞 Call Support: +91 8884174000
            </div>
          </div>
        </div>
      </div>

      <SignupModal
        isOpen={signupOpen}
        toggle={() => setSignupOpen(false)}
        branch={userData?.Branch}
      />
    </Container>
  );
};

export default LibraryPricingCard;